import React from 'react';
import { useTranslation } from 'react-i18next';
import { BsShift } from 'react-icons/bs';
import iso6391 from 'iso-639-1';
import SimpleKeyboardLayouts from 'simple-keyboard-layouts';
import { Box, Button, SvgIcon, SvgIconButton, useTheme } from '@kis-technologies/kis-ui';
import { ForwardNavigationButton } from 'components/ForwardNavigationButton';
import { Suggestions } from './Suggestions';
import { LOWER_CASE, UPPER_CASE } from '../constants';
import { KeyboardContainer, KeyboardRow, Key, useStyles, CaretContainer } from '../styled';
import { KeysValue, KeyboardProps } from '../types';
import { getAllKeys, updateKeys, frCALayout, ptBRLayout, ptPTLayout } from '../utils';

/**
 * Virtual Keyboard to type in characters
 */

export const Keyboard = (props: KeyboardProps) => {
  const {
    onClick,
    suggestions,
    onNext,
    isNextDisabled,
    hideForwardPrompt,
    nextText,
    prevText,
    onPrev,
    onCaretClick,
    onClickOutside,
    onSuggestionClick,
    language
  } = props;

  const theme = useTheme();
  const fontFamily = theme?.typography?.fontFamily || 'Poppins, sans-serif';

  const [keysValue, setKeysValue] = React.useState<KeysValue>(LOWER_CASE);

  // Get the corresponding keyboard layout of the provided language
  let lang = language ? iso6391.getName(language.substring(0, 2)).toLowerCase() : 'english';

  // Use the 'english' layout for languages that use double-byte characters
  if (['hiIN', 'jaJP', 'koKR', 'viVN', 'zhCN'].includes(language || '')) {
    lang = 'english';
  }

  const layouts = new SimpleKeyboardLayouts();
  let layout: any;
  if (language === 'ptBR') {
    layout = ptBRLayout;
  } else if (language === 'ptPT') {
    layout = ptPTLayout;
  } else if (language === 'frCA') {
    layout = frCALayout;
  } else {
    layout = layouts.get(lang);
  }

  if (!layout) layout = layouts.get('english');

  const defaultKeys = getAllKeys(layout.layout.default);
  const shiftKeys = getAllKeys(layout.layout.shift);

  const keys = React.useMemo(() => updateKeys(keysValue, defaultKeys, shiftKeys), [keysValue, defaultKeys, shiftKeys]);

  const wrapperRef = React.useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: any) => {
    if (event.target.tagName !== 'INPUT' && wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      if (onClickOutside) onClickOutside(event);
    }
  };

  React.useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    document.addEventListener('keydown', handleKeyBoardClick, true);
    return () => {
      document.removeEventListener('keydown', handleKeyBoardClick, true);
      document.removeEventListener('click', handleClickOutside, true);
    };
  });

  const handleKeyBoardClick = async (event: any) => {
    const value = event.key;
    const code = event.keyCode;
    if (onClick) {
      if ((event.ctrlKey || event.metaKey) && code === 86) {
        const text = await navigator.clipboard.readText();
        onClick(event, text);
      } else if (
        code === 32 ||
        (code >= 48 && code <= 90) ||
        (code >= 96 && code <= 111) ||
        (code >= 186 && code <= 222)
      ) {
        onClick(event, value);
      } else if (code === 8) {
        onClick(event, 'delete');
      }
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const value = event.currentTarget.value;
    if (onClick) onClick(event, value);
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
    // Prevent the element from stealing focus from the input field
    event.preventDefault();
  };

  const handleSuggestionClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const value = event.currentTarget.value;
    if (onSuggestionClick) onSuggestionClick(event, value);
  };

  const handleNext = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (onNext) onNext(event);
  };

  const handlePrev = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (onPrev) onPrev(event);
  };

  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <KeyboardContainer ref={wrapperRef}>
      {/* Suggestions */}
      <Suggestions suggestions={suggestions} onClick={handleSuggestionClick} />

      {onCaretClick && (
        <CaretContainer>
          <SvgIconButton
            sx={{
              boxShadow: 'none'
            }}
            customTransition="'opacity 0.2s linear'"
            data-testid="keyboard-caret"
            onClick={onCaretClick}
          >
            <SvgIcon name="chevron_down" size={40} />
          </SvgIconButton>
        </CaretContainer>
      )}

      {/* Spacing below the email suggestions and/or minimize button */}
      {(suggestions || onCaretClick) && <Box height={30} />}

      {/* Keyboard First Row */}
      <KeyboardRow first>
        {keys.rowOne.map((key, idx) => (
          <Key key={idx} onClick={handleClick} onMouseDown={handleMouseDown} value={key} fontFamily={fontFamily}>
            {key}
          </Key>
        ))}
      </KeyboardRow>

      {/* Keyboard Second Row */}
      <KeyboardRow>
        {keys.rowTwo.map((key, idx) => (
          <Key key={idx} onClick={handleClick} onMouseDown={handleMouseDown} value={key} fontFamily={fontFamily}>
            {key}
          </Key>
        ))}
      </KeyboardRow>

      {/* Keyboard Third Row */}
      <KeyboardRow>
        <Key
          onClick={() => setKeysValue(keysValue === LOWER_CASE ? UPPER_CASE : LOWER_CASE)}
          onMouseDown={handleMouseDown}
          fontFamily={fontFamily}
        >
          <BsShift />
        </Key>
        {keys.rowThree.map((key, idx) => (
          <Key key={idx} onClick={handleClick} onMouseDown={handleMouseDown} value={key} fontFamily={fontFamily}>
            {key}
          </Key>
        ))}
        <Key
          style={{ minWidth: '110px' }}
          onClick={handleClick}
          onMouseDown={handleMouseDown}
          value="delete"
          data-testid="delete"
          fontFamily={fontFamily}
        >
          <SvgIcon name="delete" />
        </Key>
      </KeyboardRow>

      {/* Keyboard Fourth Row */}
      <KeyboardRow first>
        {
          <Key width={226} onClick={handleClick} onMouseDown={handleMouseDown} value=".com" fontFamily={fontFamily}>
            .com
          </Key>
        }
        {keys.rowFour.map((key, idx) => (
          <Key key={idx} onClick={handleClick} onMouseDown={handleMouseDown} value={key} fontFamily={fontFamily}>
            {key}
          </Key>
        ))}
        {
          <Key width={226} onClick={handleClick} onMouseDown={handleMouseDown} value="@" fontFamily={fontFamily}>
            @
          </Key>
        }
      </KeyboardRow>

      {/* Keyboard Fifth Row */}
      <KeyboardRow>
        {prevText && (
          <Button
            customSize="x-large"
            onClick={handlePrev}
            appearance="white"
            data-testid="prev-button"
            startIconName="prev_circle"
            className={classes.keyboardPreviousButton}
          >
            {prevText}
          </Button>
        )}
        <Key
          width={350}
          onClick={handleClick}
          onMouseDown={handleMouseDown}
          value="clear"
          data-testid="clear"
          fontFamily={fontFamily}
        >
          {t('commonText.clear')}
        </Key>
        <Key width={940} onClick={handleClick} onMouseDown={handleMouseDown} value=" " fontFamily={fontFamily}>
          {t('commonText.space')}
        </Key>
        {hideForwardPrompt ? (
          <Button
            customSize="x-large"
            onClick={handleNext}
            disabled={isNextDisabled}
            appearance="primary"
            data-testid="sign-in-button"
            endIconName="next_circle"
            className={classes.keyboardNextButton}
          >
            {nextText}
          </Button>
        ) : (
          <ForwardNavigationButton
            label={nextText}
            isDisabled={isNextDisabled}
            onClick={handleNext}
            overflow="hidden"
            testId="sign-in-button"
          />
        )}
      </KeyboardRow>
    </KeyboardContainer>
  );
};
